/**
 * Buttons, cards, chips, notices, state views — the small pieces every screen
 * assembles from. The DAC library's components (design spec §2), restated for
 * the desktop portal.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'

import { Icon, type IconName } from './Icon.tsx'
import { isFailedFetch } from '../app/errors.ts'
import type { Tone } from '../deca/estado.ts'
import { formatHistoryValue } from '../deca/format.ts'

/* ── Button ─────────────────────────────────────────────────────────────── */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: 'sm' | 'md' | 'lg'
  icon?: IconName
  block?: boolean
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, block, loading, children, className, ...rest },
  ref,
) {
  const classes = [
    'btn',
    `btn--${variant}`,
    size !== 'md' ? `btn--${size}` : '',
    block ? 'btn--block' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button ref={ref} type="button" className={classes} aria-busy={loading} {...rest}>
      {icon ? <Icon name={loading ? 'refresh' : icon} size={16} /> : null}
      {children}
    </button>
  )
})

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName
  /** Required: an icon-only control must still announce what it does. */
  label: string
}

export function IconButton({ icon, label, className, ...rest }: IconButtonProps) {
  return (
    <button
      type="button"
      className={['btn', 'btn--icon', className ?? ''].filter(Boolean).join(' ')}
      aria-label={label}
      title={label}
      {...rest}
    >
      <Icon name={icon} size={18} />
    </button>
  )
}

/* ── Card ───────────────────────────────────────────────────────────────── */

/**
 * The numbered badge on a section heading — ①…⑤.
 *
 * The numbers are the PDF's own section order (design spec §7), so the portal,
 * the printed document and the import template all count the same sections in
 * the same order. Decorative here: the number is already in the heading text
 * for anyone reading it aloud.
 */
export function SectionNumber({ n }: { n: number }) {
  return (
    <span className="section-number" aria-hidden="true">
      {n}
    </span>
  )
}

export function Card({
  title,
  subtitle,
  actions,
  children,
  flush,
  id,
}: {
  title?: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
  flush?: boolean
  id?: string
}) {
  return (
    <section className="card" id={id} aria-labelledby={id ? `${id}-title` : undefined}>
      {title || actions ? (
        <header className="card__header">
          <div>
            {/* A card can carry an action without a title — the page heading
                above it already names the thing. An empty heading would be
                announced as one. */}
            {title ? (
              <h2 className="section-card__title" id={id ? `${id}-title` : undefined}>
                {title}
              </h2>
            ) : null}
            {subtitle ? <p className="small muted">{subtitle}</p> : null}
          </div>
          {actions ? <div className="row">{actions}</div> : null}
        </header>
      ) : null}
      <div className={flush ? 'card__body card__body--flush' : 'card__body'}>{children}</div>
    </section>
  )
}

/* ── Chip ───────────────────────────────────────────────────────────────── */

export function Chip({
  tone,
  icon,
  children,
  muted,
  outline,
}: {
  tone: Tone
  icon?: IconName
  children: ReactNode
  muted?: boolean
  outline?: boolean
}) {
  const classes = ['chip', `chip--${tone}`, muted ? 'chip--muted' : '', outline ? 'chip--outline' : '']
    .filter(Boolean)
    .join(' ')
  return (
    <span className={classes}>
      {icon ? <Icon name={icon} size={14} /> : null}
      {children}
    </span>
  )
}

/* ── Notice ─────────────────────────────────────────────────────────────── */

const NOTICE_ICON: Record<Tone, IconName> = {
  success: 'checkCircle',
  warning: 'alert',
  danger: 'alert',
  info: 'info',
  neutral: 'info',
}

export function Notice({
  tone = 'info',
  title,
  children,
  actions,
}: {
  tone?: Tone
  title?: ReactNode
  children?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className={`notice notice--${tone}`} role={tone === 'danger' ? 'alert' : undefined}>
      <Icon name={NOTICE_ICON[tone]} size={18} />
      <div className="notice__body">
        {title ? <span className="notice__title">{title}</span> : null}
        {children}
        {actions ? <div className="row wrap">{actions}</div> : null}
      </div>
    </div>
  )
}

/* ── state views ────────────────────────────────────────────────────────── */

export function EmptyState({
  icon = 'file',
  title,
  body,
  action,
}: {
  icon?: IconName
  title: string
  body?: string
  action?: ReactNode
}) {
  return (
    <div className="state">
      <div className="state__icon">
        <Icon name={icon} size={24} />
      </div>
      <p className="state__title">{title}</p>
      {body ? <p className="small">{body}</p> : null}
      {action}
    </div>
  )
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation()
  // The error's own text is the server's localised `detail` when it answered; when it did not, it is the
  // browser's, in English, and says nothing a reader of either language can act on.
  const message = isFailedFetch(error) ? t('errors.network') : error instanceof Error ? error.message : String(error)
  return (
    <div className="state" role="alert">
      <div className="state__icon">
        <Icon name="alert" size={24} />
      </div>
      <p className="state__title">{t('state.errorTitle')}</p>
      <p className="small">{message || t('state.errorBody')}</p>
      {onRetry ? (
        <Button icon="refresh" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      ) : null}
    </div>
  )
}

export function NoAccessState() {
  const { t } = useTranslation()
  return <EmptyState icon="ban" title={t('state.noAccessTitle')} body={t('state.noAccessBody')} />
}

export function LoadingRows({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  const { t } = useTranslation()
  return (
    <div className="stack-sm" style={{ padding: 'var(--space-xl)' }} aria-busy="true">
      <span className="sr-only">{t('common.loading')}</span>
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="row" style={{ gap: 'var(--space-lg)' }}>
          {Array.from({ length: columns }, (_, column) => (
            <div
              key={column}
              className="skeleton"
              style={{ flex: column === 0 ? '0 0 140px' : 1 }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

export function ProgressBar({ value }: { value?: number }) {
  const indeterminate = value == null
  return (
    <div
      className={indeterminate ? 'progress progress--indeterminate' : 'progress'}
      role="progressbar"
      aria-valuenow={indeterminate ? undefined : Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="progress__fill" style={indeterminate ? undefined : { width: `${value}%` }} />
    </div>
  )
}

/* ── CopyButton ─────────────────────────────────────────────────────────── */

export function CopyButton({
  value,
  label,
  size = 'sm',
  variant = 'ghost',
}: {
  value: string
  label?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: ButtonVariant
}) {
  const { t } = useTranslation()
  const [outcome, setOutcome] = useState<'copied' | 'failed' | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // A timer left running when the control goes would set state on nothing, and under test would fire after
  // the page has been torn down.
  useEffect(() => () => clearTimeout(timer.current), [])

  const copy = useCallback(async () => {
    clearTimeout(timer.current)
    let result: 'copied' | 'failed' = 'copied'
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      // Refused (no permission, a page that is not focused), or no clipboard at all: outside a secure context
      // `navigator.clipboard` does not exist, so the call throws before it can reject. Either way the text is
      // still on screen, and the user is told to take it by hand rather than left to think it was copied.
      result = 'failed'
    }
    setOutcome(result)
    timer.current = setTimeout(() => setOutcome(null), result === 'copied' ? 1800 : 8000)
  }, [value])

  // The defaults are what every existing caller had hard-coded, so this stays a
  // small ghost button everywhere except where a caller asks otherwise — which
  // today is the QR panel, where being unfindable was the defect (#83).
  return (
    <>
      <Button
        size={size}
        variant={variant}
        icon={outcome === 'copied' ? 'check' : 'copy'}
        onClick={() => void copy()}
      >
        {outcome === 'copied' ? t('common.copied') : (label ?? t('common.copy'))}
      </Button>
      {outcome === 'failed' ? (
        <span className="copy-failed" role="alert">
          {t('common.copyFailed')}
        </span>
      ) : null}
    </>
  )
}

/* ── read-only datum ────────────────────────────────────────────────────── */

export function Datum({
  label,
  value,
  hint,
  tabular,
  modified,
  previousValue,
  previousHasTime,
}: {
  label: ReactNode
  value: ReactNode
  /** A secondary line under the value — the concrete date behind a policy window. */
  hint?: ReactNode
  tabular?: boolean
  /** Changed by the most recent amendment — mirrors the PDF's yellow highlight. */
  modified?: boolean
  previousValue?: string | null
  /** Whether [previousValue], a date, carried an hour, when the history entry says (#135). */
  previousHasTime?: boolean | null
}) {
  const { t, i18n } = useTranslation()
  const empty = value === null || value === undefined || value === ''
  return (
    <div className={modified ? 'datum datum--modified' : 'datum'}>
      <span className="datum__label">{label}</span>
      <span className={`datum__value${empty ? ' datum__value--empty' : ''}${tabular ? ' tabular' : ''}`}>
        {empty ? t('common.none') : value}
      </span>
      {hint ? <span className="small muted">{hint}</span> : null}
      {previousValue ? (
        <span className="small muted">
          <s>{formatHistoryValue(previousValue, i18n.language, previousHasTime)}</s> · {t('detail.noLongerValid')}
        </span>
      ) : null}
    </div>
  )
}
